'use client';

import React from 'react';
import { Icon } from '@/components';
import { TIconName } from '@/design-tokens';
import { cn } from '@/utils';
import { ARIA_LABELS } from '@/constants';

const { COLLAPSE_NAVIGATION, EXPAND_NAVIGATION } = ARIA_LABELS;

export interface ToggleButtonProps {
  /**
   * Icon to display when the button is in collapsed state
   */
  collapsedIcon?: TIconName;
  /**
   * Icon to display when the button is in expanded state
   */
  expandedIcon?: TIconName;
  /**
   * Indicates whether the button is in expanded state
   */
  isExpanded: boolean;
  /**
   * Click handler for the button
   */
  onClick: () => void;
}

/**
 * ToggleButton component for expanding/collapsing navigation or other UI elements.
 * Supports customizable icons for both states.
 */
export const ToggleButton: React.FC<ToggleButtonProps> = ({
  collapsedIcon = 'ArrowSquareRight',
  expandedIcon = 'ArrowSquareLeft',
  isExpanded,
  onClick,
}) => {
  /**
   * Class names for the toggle button, including focus and transition styles.
   */
  const classNames = cn(
    'flex shrink-0 items-center justify-end',
    'focus-visible:ring-2 focus-visible:ring-neutral-white focus-visible:ring-offset-2',
    'h-6 w-full',
    'outline-none',
    'rounded',
    'hover:bg-white/10 focus:bg-white/10',
    'transition-all duration-200',
  );

  // Render logic
  return (
    <button
      aria-label={isExpanded ? COLLAPSE_NAVIGATION : EXPAND_NAVIGATION}
      className={classNames}
      onClick={onClick}
      type="button"
    >
      <Icon
        className="pointer-events-none"
        color="white"
        name={isExpanded ? expandedIcon : collapsedIcon}
        size={24}
      />
    </button>
  );
};

ToggleButton.displayName = 'ToggleButton';

export default ToggleButton;
